import { runInAction, when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  ConfigFieldOption,
  Domain,
  DonationForm,
  IFormNotificationSettings,
} from '@feathr/blackbox';
import { FIELD_NAMES } from '@feathr/blackbox';
import { showError, toast } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import { clearDefaultStylingOnOtherForms } from './useDonationFormBuilder.utils';

type AmountsArray = ConfigFieldOption[] & {
  add: (amount: { description: string; value: string; id?: string }) => void;
};

interface IDonationFormBuilderContext {
  amounts: AmountsArray;
  monthlyAmounts: AmountsArray;
  donationForm: DonationForm;
  formName: string;
  handleChangeMinAmount: (amount: number) => void;
  handleSave: () => Promise<void>;
  isDirty: boolean;
  isValid: boolean;
  isEmbedModalOpen: boolean;
  openEmbedModal: () => void;
  closeEmbedModal: () => void;
  minValue: number | undefined;
  lastSaved: string | undefined;
  availableContentDomains: Domain[] | undefined;
  isLoadingDomains: boolean;
  allowMonthlyDonations: boolean;
  setAllowMonthlyDonations: (value: boolean) => void;
  allowDescriptions: boolean;
  setAllowDescriptions: (value: boolean) => void;
  donationFrequency: 'one-time' | 'monthly';
  setDonationFrequency: (frequency: 'one-time' | 'monthly') => void;
  editorConfig: Record<string, unknown> | null;
  editorIsDirty: boolean;
  setEditorConfig: (
    config: Record<string, unknown>,
    notificationSettings: IFormNotificationSettings | undefined,
    isDirty: boolean,
  ) => void;
}

const DonationFormBuilderContext = createContext<IDonationFormBuilderContext | undefined>(
  undefined,
);

interface IProviderProps {
  children: ReactNode;
  donationForm: DonationForm;
}

export const DonationFormBuilderProvider = observer(function DonationFormBuilderProvider({
  children,
  donationForm,
}: Readonly<IProviderProps>): JSX.Element {
  const { t } = useTranslation();
  const { DonationForms, Domains } = useStore();

  // Helper to add one-time amounts
  const addAmount = useCallback(
    (amount: { description: string; value: string; id?: string }) => {
      const field = donationForm.configManager.getOrCreateField(FIELD_NAMES.DONATION_AMOUNT);
      field.addOption(amount);
    },
    [donationForm],
  );

  // Helper to add monthly amounts
  const addMonthlyAmount = useCallback(
    (amount: { description: string; value: string; id?: string }) => {
      const field = donationForm.configManager.getOrCreateField(
        FIELD_NAMES.MONTHLY_DONATION_AMOUNT,
      );
      field.addOption(amount);
    },
    [donationForm],
  );

  // Track pending state so the component re-renders when initial fetch completes
  const isPending = donationForm.isPending;

  // Track configManager instance - it gets replaced after save in patchDirty()
  // This causes stale field references, so we need to detect instance changes
  const configManager = donationForm.configManager;

  // Track options length from the latest field instance in render
  const donationAmountOptionsLength = configManager.getOrCreateField(FIELD_NAMES.DONATION_AMOUNT)
    .options.length;

  const monthlyDonationAmountOptionsLength = configManager.getOrCreateField(
    FIELD_NAMES.MONTHLY_DONATION_AMOUNT,
  ).options.length;

  // Create reactive amounts array with add method from the latest field instance
  // IMPORTANT: configManager can be replaced after save, so we track it as a dependency
  const amounts = useMemo(() => {
    const field = configManager.getOrCreateField(FIELD_NAMES.DONATION_AMOUNT);
    const arr = field.options as AmountsArray;
    arr.add = addAmount;
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addAmount, donationAmountOptionsLength, isPending, configManager]);

  // Create reactive monthly amounts array with add method
  const monthlyAmounts = useMemo(() => {
    const field = configManager.getOrCreateField(FIELD_NAMES.MONTHLY_DONATION_AMOUNT);
    const arr = field.options as AmountsArray;
    arr.add = addMonthlyAmount;
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addMonthlyAmount, monthlyDonationAmountOptionsLength, isPending, configManager]);

  // IMPORTANT: Access all reactive values in the component body (outside useMemo)
  // This ensures MobX tracks them and re-renders this component when they change
  // When this component re-renders, it creates a new context value, triggering consumer re-renders
  const formName = donationForm.name;
  const minValue = donationForm.minimumAmount;
  const lastSaved = donationForm.get('date_last_modified');

  const [isEmbedModalOpen, setIsEmbedModalOpen] = useState(false);
  const [isLoadingDomains, setIsLoadingDomains] = useState<boolean>(true);
  const [availableContentDomains, setAvailableContentDomains] = useState<Domain[] | undefined>(
    undefined,
  );

  // Access configManager properties directly
  const allowMonthlyDonations = donationForm.configManager.allowMonthlyDonations;
  const allowDescriptions = donationForm.configManager.allowDescriptions;
  const [donationFrequency, setDonationFrequency] = useState<'one-time' | 'monthly'>('one-time');

  // Editor iframe config state (received via postMessage from golden-goose)
  const [editorConfigData, setEditorConfigData] = useState<Record<string, unknown> | null>(null);
  const [editorNotificationSettings, setEditorNotificationSettings] =
    useState<IFormNotificationSettings | null>(null);
  const [editorIsDirty, setEditorIsDirty] = useState(false);

  const setEditorConfig = useCallback(
    (
      config: Record<string, unknown>,
      notificationSettings: IFormNotificationSettings | undefined,
      isDirtyFromEditor: boolean,
    ): void => {
      setEditorConfigData(config);
      setEditorNotificationSettings(notificationSettings ?? null);
      setEditorIsDirty(isDirtyFromEditor);
    },
    [],
  );

  // isDirty is true if either the MobX model or the editor iframe has unsaved changes
  const isDirty = donationForm.isDirty || editorIsDirty;

  // Validation is handled by the golden-goose editor
  const isValid = true;

  // Setters update configManager directly
  const setAllowMonthlyDonations = useCallback(
    (value: boolean): void => {
      donationForm.configManager.allowMonthlyDonations = value;
    },
    [donationForm],
  );

  const setAllowDescriptions = useCallback(
    (value: boolean): void => {
      donationForm.configManager.allowDescriptions = value;
    },
    [donationForm],
  );

  /**
   * Pre-load and check domains when the form builder mounts
   */
  useEffect(() => {
    async function loadDomains(): Promise<void> {
      setIsLoadingDomains(true);
      try {
        const domains = Domains.list();
        await when(() => !domains.isPending);

        if (domains.isErrored) {
          setAvailableContentDomains([]);
          return;
        }

        // Store the actual filtered domain objects in context
        const availableDomains = domains.models.filter((domain) => domain.isContentServingReady);
        setAvailableContentDomains(availableDomains);
      } catch (error) {
        setAvailableContentDomains([]);
      } finally {
        setIsLoadingDomains(false);
      }
    }

    loadDomains();
  }, [Domains]);

  const handleSave = useCallback(async (): Promise<void> => {
    try {
      runInAction(() => {
        // Apply editor config to donation form before saving
        if (editorConfigData?.config) {
          donationForm.set({ config: editorConfigData.config as DonationForm['config'] });
          // Force configManager to rebuild now while we're still in the action context
          // This prevents MobX strict-mode errors when the getter is accessed later
          Boolean(donationForm.configManager);
        }

        // Apply notification settings if provided
        if (editorNotificationSettings) {
          donationForm.set({ notification_settings: editorNotificationSettings });
        }
      });

      const response = await donationForm.patchDirty();

      if (response.isErrored) {
        throw new Error(response.error?.message);
      }

      // Reset editor dirty state after successful save
      setEditorIsDirty(false);

      // If this form is now the default, clear defaultStyling on other forms
      // The editor sends config nested in a 'config' key: { config: { settings: {...}, style: {...} } }
      const formConfig = editorConfigData?.config as Record<string, unknown> | undefined;
      const settings = formConfig?.settings as Record<string, unknown> | undefined;
      if (settings?.defaultStyling && donationForm.id) {
        // Fire-and-forget - don't block the save completion
        clearDefaultStylingOnOtherForms(DonationForms, donationForm.id).catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Failed to clear defaultStyling on other forms:', error);
        });
      }

      toast(t('Changes saved.'), { type: 'success' });
    } catch (e) {
      showError(e, t);
    }
  }, [DonationForms, donationForm, editorConfigData, editorNotificationSettings, t]);

  const openEmbedModal = useCallback((): void => {
    setIsEmbedModalOpen(true);
  }, []);

  const closeEmbedModal = useCallback((): void => {
    setIsEmbedModalOpen(false);
  }, []);

  const handleChangeMinAmount = useCallback(
    (amount: number): void => {
      donationForm.setMinimumAmount(amount);
    },
    [donationForm],
  );

  // Memoize the context value with all dependencies
  // This prevents unnecessary re-renders of consumers when unrelated props change
  const value = useMemo(
    () => ({
      amounts,
      monthlyAmounts,
      donationForm,
      formName,
      handleChangeMinAmount,
      handleSave,
      isDirty,
      isValid,
      isEmbedModalOpen,
      openEmbedModal,
      closeEmbedModal,
      minValue,
      lastSaved,
      availableContentDomains,
      isLoadingDomains,
      allowMonthlyDonations,
      setAllowMonthlyDonations,
      allowDescriptions,
      setAllowDescriptions,
      donationFrequency,
      setDonationFrequency,
      editorConfig: editorConfigData,
      editorIsDirty,
      setEditorConfig,
    }),
    [
      amounts,
      monthlyAmounts,
      donationForm,
      formName,
      handleChangeMinAmount,
      handleSave,
      isDirty,
      isValid,
      isEmbedModalOpen,
      openEmbedModal,
      closeEmbedModal,
      minValue,
      lastSaved,
      availableContentDomains,
      isLoadingDomains,
      allowMonthlyDonations,
      setAllowMonthlyDonations,
      allowDescriptions,
      setAllowDescriptions,
      donationFrequency,
      editorConfigData,
      editorIsDirty,
      setEditorConfig,
    ],
  );

  return (
    <DonationFormBuilderContext.Provider value={value}>
      {children}
    </DonationFormBuilderContext.Provider>
  );
});

export function useDonationFormBuilder(): IDonationFormBuilderContext {
  const context = useContext(DonationFormBuilderContext);
  if (context === undefined) {
    throw new Error('useDonationFormBuilder must be used within DonationFormBuilderProvider');
  }
  return context;
}
